<script setup>
import { useApi } from '@/utils/api'

definePage({
  meta: {
    layout: 'blank',
    action: 'read',
    subject: 'Sales',
  },
})

/**
 * pages/pos/qris-print/[id].vue — Modul 18
 * ─────────────────────────────────────────────────────────
 * Halaman cetak QRIS terpisah dari struk. Dibuka di tab baru dari
 * PosQrisPaymentDialog ("Cetak QR") supaya kasir bisa cetak QR untuk
 * ditunjukkan/diserahkan ke customer — customer TIDAK bisa scan QR
 * langsung dari layar komputer kasir.
 *
 * :id di sini adalah order_id. Ambil transaksi PENDING terbaru untuk
 * order tsb (bukan bikin charge baru — cuma menampilkan qr_string yang
 * sudah ada, sama seperti bagian read-only di halaman detail order).
 */

const route = useRoute()
const router = useRouter()
const { formatRupiah, formatDate } = useFormat()

const orderId = route.params.id

const order = ref(null)
const transaction = ref(null)
const tenant = ref(null)
const loading = ref(true)
const loadError = ref(null)
const qrCanvasRef = ref(null)
const isUrlBasedQr = ref(false)

const loadData = async () => {
  loading.value = true
  loadError.value = null

  try {
    const [orderRes, txRes, settingsRes] = await Promise.all([
      useApi(`/api/v1/orders/${orderId}`).get(),
      useApi(`/api/v1/orders/${orderId}/payment-transactions`).get(),
      useApi('/api/v1/settings').get(),
    ])

    order.value = orderRes?.data ?? orderRes
    tenant.value = (settingsRes?.data ?? settingsRes)?.tenant ?? null

    const transactions = txRes?.data ?? txRes ?? []

    // Transaksi terbaru yang statusnya masih pending (yang perlu di-scan)
    transaction.value = transactions.find(tx => tx.status === 'pending') ?? transactions[0] ?? null

    // Addendum 06 (iPaymu): provider ini tidak mengembalikan raw QRIS
    // string, cuma invoice_url (URL checkout hosted) — tetap di-encode jadi
    // QR yang sama supaya halaman cetak ini tidak perlu tahu bedanya.
    const qrSource = transaction.value?.qr_string || transaction.value?.invoice_url

    if (!qrSource) {
      loadError.value = 'Belum ada QR pembayaran untuk order ini. Buat transaksi QRIS terlebih dahulu di POS.'

      return
    }

    isUrlBasedQr.value = !transaction.value?.qr_string && !!transaction.value?.invoice_url

    // ⚠️ FIX: canvas ada di dalam <template v-else> yang baru render setelah
    // loading=false. Sebelumnya loading cuma di-set false di finally (di
    // BAWAH panggilan toCanvas), jadi qrCanvasRef.value masih null saat
    // toCanvas dipanggil — QRCode.toCanvas(null, ...) tidak error, tapi
    // menggambar ke canvas baru yang tidak pernah menempel ke DOM, hasilnya
    // area QR di halaman selalu kosong. Set loading=false DULU di sini,
    // baru nextTick + gambar ke canvas yang sudah benar-benar ada di DOM.
    loading.value = false
    await nextTick()

    const QRCode = await import('qrcode')

    await QRCode.toCanvas(qrCanvasRef.value, qrSource, { width: 300, margin: 1 })

    // Auto-trigger print setelah QR selesai dirender — tujuan halaman ini
    // memang untuk langsung dicetak begitu dibuka.
    await nextTick()
    window.print()
  } catch (e) {
    loadError.value = e?.data?.message ?? 'Gagal memuat data QR pembayaran.'
  } finally {
    loading.value = false
  }
}

onMounted(loadData)

const printBrowser = () => {
  window.print()
}

const backToPos = () => {
  router.push('/pos')
}
</script>

<template>
  <div class="qris-print-page">
    <div v-if="loading" class="d-flex justify-center align-center" style="min-height: 60vh;">
      <VProgressCircular indeterminate color="primary" />
    </div>

    <VAlert v-else-if="loadError" type="error" variant="tonal" class="ma-4 d-print-none">
      {{ loadError }}
      <template #append>
        <VBtn size="small" variant="text" @click="backToPos">Kembali ke POS</VBtn>
      </template>
    </VAlert>

    <template v-else>
      <VCard max-width="380" class="mx-auto my-4">
        <div id="qris-print-area" class="qris-print pa-4 text-center">
          <div class="mb-2">
            <div class="text-h6 font-weight-bold">{{ tenant?.name ?? '-' }}</div>
            <div v-if="tenant?.address" class="text-caption">{{ tenant.address }}</div>
          </div>

          <VDivider class="my-2 qris-divider" />

          <div class="text-caption d-flex justify-space-between">
            <span>No. Order</span>
            <span>{{ order?.order_number }}</span>
          </div>
          <div class="text-caption d-flex justify-space-between">
            <span>Waktu</span>
            <span>{{ formatDate(transaction?.created_at, { hour: '2-digit', minute: '2-digit' }) }}</span>
          </div>

          <VDivider class="my-3 qris-divider" />

          <div class="text-h6 font-weight-bold mb-3">
            {{ formatRupiah(transaction?.gross_amount ?? order?.total ?? 0) }}
          </div>

          <canvas ref="qrCanvasRef" class="mx-auto d-block mb-2" />

          <div class="text-caption text-medium-emphasis">
            <template v-if="isUrlBasedQr">
              Scan QR ini menggunakan kamera HP untuk membuka halaman pembayaran.
            </template>
            <template v-else>
              Scan QR ini menggunakan aplikasi e-wallet/m-banking untuk membayar.
            </template>
          </div>
          <div v-if="transaction?.expired_at" class="text-caption text-medium-emphasis mt-1">
            Berlaku sampai {{ formatDate(transaction.expired_at, { hour: '2-digit', minute: '2-digit' }) }}
          </div>

          <div class="text-center text-caption mt-4">
            Terima kasih!
          </div>
        </div>

        <VDivider />

        <VCardActions class="d-print-none flex-column ga-2 pa-4">
          <VBtn color="primary" block prepend-icon="ri-printer-line" @click="printBrowser">
            Cetak Ulang
          </VBtn>
          <VBtn variant="text" block prepend-icon="ri-arrow-left-line" @click="backToPos">
            Kembali ke POS
          </VBtn>
        </VCardActions>
      </VCard>
    </template>
  </div>
</template>

<style scoped>
.qris-print {
  font-family: 'Courier New', monospace;
}
.qris-divider {
  border-style: dashed;
}

@media print {
  @page {
    /* Beri tahu browser ukuran kertas SEBENARNYA (roll continuous 58mm),
       supaya tidak bergantung pada "Custom Scale" manual di dialog print
       yang gampang salah setting dan bikin QR jadi kelebihan besar/terpotong. */
    size: 58mm auto;
    margin: 0;
  }

  .d-print-none {
    display: none !important;
  }
  .qris-print-page {
    background: #fff;
  }

  /* Container dipaksa selebar area cetak 58mm (dikurangi margin kertas
     printer thermal ~2mm kiri-kanan), bukan ikut lebar layar (380px). */
  .qris-print-page :deep(.v-card) {
    width: 54mm !important;
    max-width: 54mm !important;
    box-shadow: none !important;
    border-radius: 0 !important;
  }
  .qris-print {
    width: 100% !important;
    padding: 2mm !important;
    font-size: 9px;
  }
  .qris-print .text-h6 {
    font-size: 12px !important;
  }

  /* Canvas QR: resolusi internal (300x300px, diset lewat QRCode.toCanvas)
     tetap tinggi supaya tajam, tapi UKURAN TAMPIL dipaksa proporsional ke
     kertas 58mm — sebelumnya CSS tidak membatasi ini sama sekali, jadi
     canvas tercetak sesuai 300px aslinya (~79mm) yang lebih lebar dari
     kertasnya sendiri (58mm), makanya kelihatan kebesaran/terpotong. */
  .qris-print canvas {
    width: 36mm !important;
    height: 36mm !important;
  }
}
</style>
